<script setup>
import { ref, computed, reactive, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { required, helpers, url } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useToggle } from '@vueuse/core';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import AccessToken from 'dashboard/routes/dashboard/settings/profile/AccessToken.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const MODAL_TYPES = {
  CREATE: 'create',
  EDIT: 'edit',
};

const BOT_TYPES = {
  WEBHOOK: 'webhook',
  TYPEBOT: 'typebot',
  EVO_AI: 'evo_ai',
};

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const uiFlags = useMapGetter('agentBots/getUIFlags');

const formState = reactive({
  botType: BOT_TYPES.WEBHOOK,
  botName: '',
  botDescription: '',
  botUrl: '',
  botAvatar: null,
  botAvatarUrl: '',
  typebotApiUrl: '',
  typebotPublicName: '',
  typebotApiToken: '',
  typebotTriggerType: 'all',
  typebotTriggerOperator: 'contains',
  typebotTriggerValue: '',
  typebotExpireInMinutes: '',
  typebotDefaultDelayMessage: '',
  typebotDebounceTime: '',
  typebotFinishKeyword: '',
  typebotIgnoreGroups: false,
  typebotIgnoredTargets: [],
  evoAiAgentUrl: '',
  evoAiApiKey: '',
  evoAiAgentId: '',
  evoAiTriggerType: 'all',
  evoAiTriggerOperator: 'contains',
  evoAiTriggerValue: '',
  evoAiExpireInMinutes: '',
  evoAiDefaultDelayMessage: '',
  evoAiDebounceTime: '',
  evoAiFinishKeyword: '',
  evoAiIgnoreGroups: false,
  evoAiIgnoredTargets: [],
});

const [showAccessToken, toggleAccessToken] = useToggle();
const accessToken = ref('');

const numericValidator = helpers.withMessage(
  () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.NUMERIC'),
  value => !value || /^\d+$/.test(value)
);

const validationRules = computed(() => {
  const rules = {
    botName: {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.NAME'),
        required
      ),
    },
  };

  if (formState.botType === BOT_TYPES.WEBHOOK) {
    rules.botUrl = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.VALID_URL'),
        url
      ),
    };
  }

  if (formState.botType === BOT_TYPES.TYPEBOT) {
    rules.typebotApiUrl = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.API_URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.API_URL_INVALID'),
        url
      ),
    };
    rules.typebotPublicName = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.PUBLIC_NAME'),
        required
      ),
    };
    rules.typebotTriggerType = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.TRIGGER_TYPE'),
        required
      ),
    };

    if (formState.typebotTriggerType === 'keyword') {
      rules.typebotTriggerOperator = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.TRIGGER_OPERATOR'),
          required
        ),
      };
      rules.typebotTriggerValue = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.TYPEBOT.TRIGGER_VALUE'),
          required
        ),
      };
    }

    rules.typebotExpireInMinutes = { numericValidator };
    rules.typebotDefaultDelayMessage = { numericValidator };
    rules.typebotDebounceTime = { numericValidator };
  }

  if (formState.botType === BOT_TYPES.EVO_AI) {
    rules.evoAiAgentUrl = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.AGENT_URL'),
        required
      ),
      url: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.AGENT_URL_INVALID'),
        url
      ),
    };
    rules.evoAiApiKey = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.API_KEY'),
        required
      ),
    };
    rules.evoAiAgentId = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.AGENT_ID'),
        required
      ),
    };
    rules.evoAiTriggerType = {
      required: helpers.withMessage(
        () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.TRIGGER_TYPE'),
        required
      ),
    };

    if (formState.evoAiTriggerType === 'keyword') {
      rules.evoAiTriggerOperator = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.TRIGGER_OPERATOR'),
          required
        ),
      };
      rules.evoAiTriggerValue = {
        required: helpers.withMessage(
          () => t('AGENT_BOTS.FORM.ERRORS.EVO_AI.TRIGGER_VALUE'),
          required
        ),
      };
    }

    rules.evoAiExpireInMinutes = { numericValidator };
    rules.evoAiDefaultDelayMessage = { numericValidator };
    rules.evoAiDebounceTime = { numericValidator };
  }

  return rules;
});

const v$ = useVuelidate(validationRules, formState);

const botTypeOptions = computed(() => [
  {
    label: t('AGENT_BOTS.FORM.BOT_TYPE.WEBHOOK'),
    value: BOT_TYPES.WEBHOOK,
  },
  {
    label: t('AGENT_BOTS.FORM.BOT_TYPE.TYPEBOT'),
    value: BOT_TYPES.TYPEBOT,
  },
  {
    label: t('AGENT_BOTS.FORM.BOT_TYPE.EVO_AI'),
    value: BOT_TYPES.EVO_AI,
  },
]);

const selectedBotTypeLabel = computed(() => {
  const option = botTypeOptions.value.find(
    currentOption => currentOption.value === formState.botType
  );
  return option ? option.label : '';
});

const triggerTypeOptions = computed(() => [
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_TYPE.OPTIONS.ALL'),
    value: 'all',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_TYPE.OPTIONS.KEYWORD'),
    value: 'keyword',
  },
]);

const triggerOperatorOptions = computed(() => [
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.CONTAINS'),
    value: 'contains',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.EQUALS'),
    value: 'equals',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.STARTS_WITH'),
    value: 'starts_with',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.ENDS_WITH'),
    value: 'ends_with',
  },
  {
    label: t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.OPTIONS.REGEX'),
    value: 'regex',
  },
]);

const selectedTriggerTypeLabel = computed(() => {
  const triggerType =
    formState.botType === BOT_TYPES.EVO_AI
      ? formState.evoAiTriggerType
      : formState.typebotTriggerType;
  const option = triggerTypeOptions.value.find(
    currentOption => currentOption.value === triggerType
  );
  return option ? option.label : '';
});

const selectedTriggerOperatorLabel = computed(() => {
  const triggerOperator =
    formState.botType === BOT_TYPES.EVO_AI
      ? formState.evoAiTriggerOperator
      : formState.typebotTriggerOperator;
  const option = triggerOperatorOptions.value.find(
    currentOption => currentOption.value === triggerOperator
  );
  return option ? option.label : '';
});

const isLoading = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? uiFlags.value.isCreating
    : uiFlags.value.isUpdating
);

const dialogTitle = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.TITLE');
  }

  return props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.ADD.TITLE')
    : t('AGENT_BOTS.EDIT.TITLE');
});

const dialogDescription = computed(() => {
  if (showAccessToken.value) {
    return t('AGENT_BOTS.ACCESS_TOKEN.DESCRIPTION');
  }
  return '';
});

const confirmButtonLabel = computed(() =>
  props.type === MODAL_TYPES.CREATE
    ? t('AGENT_BOTS.FORM.CREATE')
    : t('AGENT_BOTS.FORM.UPDATE')
);

const botNameError = computed(() =>
  v$.value.botName.$error ? v$.value.botName.$errors[0]?.$message : ''
);

const botUrlError = computed(() =>
  v$.value.botUrl.$error ? v$.value.botUrl.$errors[0]?.$message : ''
);

const typebotApiUrlError = computed(() =>
  v$.value.typebotApiUrl?.$error
    ? v$.value.typebotApiUrl.$errors[0]?.$message
    : ''
);

const typebotPublicNameError = computed(() =>
  v$.value.typebotPublicName?.$error
    ? v$.value.typebotPublicName.$errors[0]?.$message
    : ''
);

const typebotTriggerTypeError = computed(() =>
  v$.value.typebotTriggerType?.$error
    ? v$.value.typebotTriggerType.$errors[0]?.$message
    : ''
);

const typebotTriggerOperatorError = computed(() =>
  v$.value.typebotTriggerOperator?.$error
    ? v$.value.typebotTriggerOperator.$errors[0]?.$message
    : ''
);

const typebotTriggerValueError = computed(() =>
  v$.value.typebotTriggerValue?.$error
    ? v$.value.typebotTriggerValue.$errors[0]?.$message
    : ''
);

const typebotExpireInMinutesError = computed(() =>
  v$.value.typebotExpireInMinutes?.$error
    ? v$.value.typebotExpireInMinutes.$errors[0]?.$message
    : ''
);

const typebotDefaultDelayMessageError = computed(() =>
  v$.value.typebotDefaultDelayMessage?.$error
    ? v$.value.typebotDefaultDelayMessage.$errors[0]?.$message
    : ''
);

const typebotDebounceTimeError = computed(() =>
  v$.value.typebotDebounceTime?.$error
    ? v$.value.typebotDebounceTime.$errors[0]?.$message
    : ''
);

const showAccessTokenInput = computed(
  () =>
    showAccessToken.value ||
    props.type === MODAL_TYPES.EDIT ||
    accessToken.value
);

const resetForm = () => {
  Object.assign(formState, {
    botType: BOT_TYPES.WEBHOOK,
    botName: '',
    botDescription: '',
    botUrl: '',
    botAvatar: null,
    botAvatarUrl: '',
    typebotApiUrl: '',
    typebotPublicName: '',
    typebotApiToken: '',
    typebotTriggerType: 'all',
    typebotTriggerOperator: 'contains',
    typebotTriggerValue: '',
    typebotExpireInMinutes: '',
    typebotDefaultDelayMessage: '',
    typebotDebounceTime: '',
    typebotFinishKeyword: '',
    typebotIgnoreGroups: false,
    typebotIgnoredTargets: [],
    evoAiAgentUrl: '',
    evoAiApiKey: '',
    evoAiAgentId: '',
    evoAiTriggerType: 'all',
    evoAiTriggerOperator: 'contains',
    evoAiTriggerValue: '',
    evoAiExpireInMinutes: '',
    evoAiDefaultDelayMessage: '',
    evoAiDebounceTime: '',
    evoAiFinishKeyword: '',
    evoAiIgnoreGroups: false,
    evoAiIgnoredTargets: [],
  });
  v$.value.$reset();
};

const handleImageUpload = ({ file, url: avatarUrl }) => {
  formState.botAvatar = file;
  formState.botAvatarUrl = avatarUrl;
};

const handleAvatarDelete = async () => {
  if (props.selectedBot?.id) {
    try {
      await store.dispatch(
        'agentBots/deleteAgentBotAvatar',
        props.selectedBot.id
      );
      formState.botAvatar = null;
      formState.botAvatarUrl = '';
      useAlert(t('AGENT_BOTS.AVATAR.SUCCESS_DELETE'));
    } catch (error) {
      useAlert(t('AGENT_BOTS.AVATAR.ERROR_DELETE'));
    }
  } else {
    formState.botAvatar = null;
    formState.botAvatarUrl = '';
  }
};

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (showAccessToken.value) return;

  const isWebhook = formState.botType === BOT_TYPES.WEBHOOK;
  const isTypebot = formState.botType === BOT_TYPES.TYPEBOT;
  const isEvoAi = formState.botType === BOT_TYPES.EVO_AI;
  const sanitizedIgnoredTargets = formState.typebotIgnoredTargets
    .map(value => value?.toString().trim())
    .filter(value => value);

  let botConfig = {};
  if (isWebhook) {
    botConfig = { webhook_url: formState.botUrl };
  } else if (isTypebot) {
    botConfig = {
      api_url: formState.typebotApiUrl,
      public_name: formState.typebotPublicName,
      api_token: formState.typebotApiToken,
      trigger_type: formState.typebotTriggerType,
      trigger_operator:
        formState.typebotTriggerType === 'keyword'
          ? formState.typebotTriggerOperator
          : '',
      trigger_value:
        formState.typebotTriggerType === 'keyword'
          ? formState.typebotTriggerValue
          : '',
      expire_in_minutes: formState.typebotExpireInMinutes,
      default_delay_message: formState.typebotDefaultDelayMessage,
      debounce_time: formState.typebotDebounceTime,
      finish_keyword: formState.typebotFinishKeyword,
      ignore_groups: formState.typebotIgnoreGroups,
      ignored_targets: sanitizedIgnoredTargets,
    };
  } else if (isEvoAi) {
    const sanitizedEvoAiIgnoredTargets = formState.evoAiIgnoredTargets
      .map(value => value?.toString().trim())
      .filter(value => value);
    botConfig = {
      agent_url: formState.evoAiAgentUrl,
      api_key: formState.evoAiApiKey,
      agent_id: formState.evoAiAgentId,
      trigger_type: formState.evoAiTriggerType,
      trigger_operator:
        formState.evoAiTriggerType === 'keyword'
          ? formState.evoAiTriggerOperator
          : '',
      trigger_value:
        formState.evoAiTriggerType === 'keyword'
          ? formState.evoAiTriggerValue
          : '',
      expire_in_minutes: formState.evoAiExpireInMinutes,
      default_delay_message: formState.evoAiDefaultDelayMessage,
      debounce_time: formState.evoAiDebounceTime,
      finish_keyword: formState.evoAiFinishKeyword,
      ignore_groups: formState.evoAiIgnoreGroups,
      ignored_targets: sanitizedEvoAiIgnoredTargets,
    };
  }

  const botData = {
    name: formState.botName,
    description: formState.botDescription,
    outgoing_url: isWebhook ? formState.botUrl : '',
    bot_type: formState.botType,
    avatar: formState.botAvatar,
    bot_config: botConfig,
  };

  const isCreate = props.type === MODAL_TYPES.CREATE;

  try {
    const actionPayload = isCreate
      ? botData
      : { id: props.selectedBot.id, data: botData };

    const response = await store.dispatch(
      `agentBots/${isCreate ? 'create' : 'update'}`,
      actionPayload
    );

    const alertKey = isCreate
      ? t('AGENT_BOTS.ADD.API.SUCCESS_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.SUCCESS_MESSAGE');
    useAlert(alertKey);

    // Show access token after creation
    if (isCreate) {
      const { access_token: responseAccessToken, id } = response || {};

      if (id && responseAccessToken) {
        accessToken.value = responseAccessToken;
        toggleAccessToken(true);
      } else {
        accessToken.value = '';
        dialogRef.value.close();
      }
    } else {
      dialogRef.value.close();
    }

    resetForm();
  } catch (error) {
    const errorKey = isCreate
      ? t('AGENT_BOTS.ADD.API.ERROR_MESSAGE')
      : t('AGENT_BOTS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorKey);
  }
};

const initializeForm = () => {
  if (props.selectedBot && Object.keys(props.selectedBot).length) {
    const {
      name,
      description,
      outgoing_url: botUrl,
      thumbnail,
      bot_config: botConfig,
      access_token: botAccessToken,
      bot_type: botType,
    } = props.selectedBot;
    formState.botType = botType || BOT_TYPES.WEBHOOK;
    formState.botName = name || '';
    formState.botDescription = description || '';
    formState.botUrl = botUrl || botConfig?.webhook_url || '';
    formState.botAvatarUrl = thumbnail || '';

    if (formState.botType === BOT_TYPES.TYPEBOT) {
      formState.typebotApiUrl = botConfig?.api_url || '';
      formState.typebotPublicName = botConfig?.public_name || '';
      formState.typebotApiToken = botConfig?.api_token || '';
      formState.typebotTriggerType = botConfig?.trigger_type || 'all';
      formState.typebotTriggerOperator =
        botConfig?.trigger_operator || 'contains';
      formState.typebotTriggerValue = botConfig?.trigger_value || '';
      formState.typebotExpireInMinutes = botConfig?.expire_in_minutes || '';
      formState.typebotDefaultDelayMessage =
        botConfig?.default_delay_message || '';
      formState.typebotDebounceTime = botConfig?.debounce_time || '';
      formState.typebotFinishKeyword = botConfig?.finish_keyword || '';
      formState.typebotIgnoreGroups =
        typeof botConfig?.ignore_groups === 'string'
          ? ['true', '1', 'yes', 'on'].includes(
              botConfig.ignore_groups.toLowerCase()
            )
          : Boolean(botConfig?.ignore_groups);
      let existingIgnoredTargets = [];
      if (Array.isArray(botConfig?.ignored_targets)) {
        existingIgnoredTargets = botConfig.ignored_targets;
      } else if (botConfig?.ignored_targets) {
        existingIgnoredTargets = [botConfig.ignored_targets];
      }
      formState.typebotIgnoredTargets = existingIgnoredTargets
        .map(value => value?.toString().trim())
        .filter(value => value);
    } else if (formState.botType === BOT_TYPES.EVO_AI) {
      formState.evoAiAgentUrl = botConfig?.agent_url || '';
      formState.evoAiApiKey = botConfig?.api_key || '';
      formState.evoAiAgentId = botConfig?.agent_id || '';
      formState.evoAiTriggerType = botConfig?.trigger_type || 'all';
      formState.evoAiTriggerOperator =
        botConfig?.trigger_operator || 'contains';
      formState.evoAiTriggerValue = botConfig?.trigger_value || '';
      formState.evoAiExpireInMinutes = botConfig?.expire_in_minutes || '';
      formState.evoAiDefaultDelayMessage =
        botConfig?.default_delay_message || '';
      formState.evoAiDebounceTime = botConfig?.debounce_time || '';
      formState.evoAiFinishKeyword = botConfig?.finish_keyword || '';
      formState.evoAiIgnoreGroups =
        typeof botConfig?.ignore_groups === 'string'
          ? ['true', '1', 'yes', 'on'].includes(
              botConfig.ignore_groups.toLowerCase()
            )
          : Boolean(botConfig?.ignore_groups);
      let existingEvoAiIgnoredTargets = [];
      if (Array.isArray(botConfig?.ignored_targets)) {
        existingEvoAiIgnoredTargets = botConfig.ignored_targets;
      } else if (botConfig?.ignored_targets) {
        existingEvoAiIgnoredTargets = [botConfig.ignored_targets];
      }
      formState.evoAiIgnoredTargets = existingEvoAiIgnoredTargets
        .map(value => value?.toString().trim())
        .filter(value => value);
      // Clear typebot fields
      formState.typebotApiUrl = '';
      formState.typebotPublicName = '';
      formState.typebotApiToken = '';
      formState.typebotTriggerType = 'all';
      formState.typebotTriggerOperator = 'contains';
      formState.typebotTriggerValue = '';
      formState.typebotExpireInMinutes = '';
      formState.typebotDefaultDelayMessage = '';
      formState.typebotDebounceTime = '';
      formState.typebotFinishKeyword = '';
      formState.typebotIgnoreGroups = false;
      formState.typebotIgnoredTargets = [];
    } else {
      formState.typebotApiUrl = '';
      formState.typebotPublicName = '';
      formState.typebotApiToken = '';
      formState.typebotTriggerType = 'all';
      formState.typebotTriggerOperator = 'contains';
      formState.typebotTriggerValue = '';
      formState.typebotExpireInMinutes = '';
      formState.typebotDefaultDelayMessage = '';
      formState.typebotDebounceTime = '';
      formState.typebotFinishKeyword = '';
      formState.typebotIgnoreGroups = false;
      formState.typebotIgnoredTargets = [];
      formState.evoAiAgentUrl = '';
      formState.evoAiApiKey = '';
      formState.evoAiAgentId = '';
      formState.evoAiTriggerType = 'all';
      formState.evoAiTriggerOperator = 'contains';
      formState.evoAiTriggerValue = '';
      formState.evoAiExpireInMinutes = '';
      formState.evoAiDefaultDelayMessage = '';
      formState.evoAiDebounceTime = '';
      formState.evoAiFinishKeyword = '';
      formState.evoAiIgnoreGroups = false;
      formState.evoAiIgnoredTargets = [];
    }

    if (botAccessToken && props.type === MODAL_TYPES.EDIT) {
      accessToken.value = botAccessToken;
    }
  } else {
    resetForm();
  }
};

const onCopyToken = async value => {
  await copyTextToClipboard(value);
  useAlert(t('AGENT_BOTS.ACCESS_TOKEN.COPY_SUCCESSFUL'));
};

const onResetToken = async () => {
  const response = await store.dispatch(
    'agentBots/resetAccessToken',
    props.selectedBot.id
  );
  if (response) {
    accessToken.value = response.access_token;
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_SUCCESS'));
  } else {
    useAlert(t('AGENT_BOTS.ACCESS_TOKEN.RESET_ERROR'));
  }
};

const closeModal = () => {
  if (!showAccessToken.value) v$.value?.$reset();
  accessToken.value = '';
  toggleAccessToken(false);
};

const onClickClose = () => {
  closeModal();
  dialogRef.value.close();
};

watch(() => props.selectedBot, initializeForm, { immediate: true, deep: true });

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="dialogTitle"
    :description="dialogDescription"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="closeModal"
  >
    <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
      <div class="flex flex-col gap-4 max-h-[65vh] overflow-y-auto pr-2">
        <div
          v-if="!showAccessToken || type === MODAL_TYPES.EDIT"
          class="flex flex-col gap-4"
        >
          <div class="mb-2 flex flex-col items-start">
            <span class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.AVATAR.LABEL') }}
            </span>
            <Avatar
              :src="formState.botAvatarUrl"
              :name="formState.botName"
              :size="68"
              allow-upload
              icon-name="i-lucide-bot-message-square"
              @upload="handleImageUpload"
              @delete="handleAvatarDelete"
            />
          </div>

          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AGENT_BOTS.FORM.BOT_TYPE.LABEL') }}
            </span>
            <SelectMenu
              :model-value="formState.botType"
              :options="botTypeOptions"
              :label="selectedBotTypeLabel"
              sub-menu-position="bottom"
              @update:model-value="value => (formState.botType = value)"
            />
          </div>

          <Input
            id="bot-name"
            v-model="formState.botName"
            :label="$t('AGENT_BOTS.FORM.NAME.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.NAME.PLACEHOLDER')"
            :message="botNameError"
            :message-type="botNameError ? 'error' : 'info'"
            @blur="v$.botName.$touch()"
          />

          <TextArea
            id="bot-description"
            v-model="formState.botDescription"
            :label="$t('AGENT_BOTS.FORM.DESCRIPTION.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.DESCRIPTION.PLACEHOLDER')"
          />

          <Input
            v-if="formState.botType === BOT_TYPES.WEBHOOK"
            id="bot-url"
            v-model="formState.botUrl"
            :label="$t('AGENT_BOTS.FORM.WEBHOOK_URL.LABEL')"
            :placeholder="$t('AGENT_BOTS.FORM.WEBHOOK_URL.PLACEHOLDER')"
            :message="botUrlError"
            :message-type="botUrlError ? 'error' : 'info'"
            @blur="v$.botUrl?.$touch()"
          />

          <div
            v-if="formState.botType === BOT_TYPES.TYPEBOT"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.SECTIONS.CONNECTION') }}
              </h3>
              <Input
                id="typebot-api-url"
                v-model="formState.typebotApiUrl"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.API_URL.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.TYPEBOT.API_URL.PLACEHOLDER')"
                :message="typebotApiUrlError"
                :message-type="typebotApiUrlError ? 'error' : 'info'"
                @blur="v$.typebotApiUrl?.$touch()"
              />
              <Input
                id="typebot-public-name"
                v-model="formState.typebotPublicName"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.PUBLIC_NAME.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.PUBLIC_NAME.PLACEHOLDER')
                "
                :message="typebotPublicNameError"
                :message-type="typebotPublicNameError ? 'error' : 'info'"
                @blur="v$.typebotPublicName?.$touch()"
              />
              <Input
                id="typebot-api-token"
                v-model="formState.typebotApiToken"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.API_TOKEN.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.API_TOKEN.PLACEHOLDER')
                "
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_SETTINGS.TITLE') }}
              </h3>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_TYPE.LABEL') }}
                </span>
                <SelectMenu
                  :model-value="formState.typebotTriggerType"
                  :options="triggerTypeOptions"
                  :label="selectedTriggerTypeLabel"
                  sub-menu-position="bottom"
                  @update:model-value="
                    value => (formState.typebotTriggerType = value)
                  "
                />
                <p v-if="typebotTriggerTypeError" class="text-xs text-n-ruby-9">
                  {{ typebotTriggerTypeError }}
                </p>
              </div>

              <div
                v-if="formState.typebotTriggerType === 'keyword'"
                class="flex flex-col gap-3"
              >
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_OPERATOR.LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="formState.typebotTriggerOperator"
                    :options="triggerOperatorOptions"
                    :label="selectedTriggerOperatorLabel"
                    sub-menu-position="bottom"
                    @update:model-value="
                      value => (formState.typebotTriggerOperator = value)
                    "
                  />
                  <p
                    v-if="typebotTriggerOperatorError"
                    class="text-xs text-n-ruby-9"
                  >
                    {{ typebotTriggerOperatorError }}
                  </p>
                </div>
                <Input
                  id="typebot-trigger-value"
                  v-model="formState.typebotTriggerValue"
                  :label="$t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_VALUE.LABEL')"
                  :placeholder="
                    $t('AGENT_BOTS.FORM.TYPEBOT.TRIGGER_VALUE.PLACEHOLDER')
                  "
                  :message="typebotTriggerValueError"
                  :message-type="typebotTriggerValueError ? 'error' : 'info'"
                  @blur="v$.typebotTriggerValue?.$touch()"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.GENERAL_SETTINGS.TITLE') }}
              </h3>
              <Input
                id="typebot-finish-keyword"
                v-model="formState.typebotFinishKeyword"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.FINISH_KEYWORD.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.FINISH_KEYWORD.PLACEHOLDER')
                "
                :message="
                  $t('AGENT_BOTS.FORM.TYPEBOT.FINISH_KEYWORD.HELP_TEXT')
                "
              />
              <Input
                id="typebot-expire"
                v-model="formState.typebotExpireInMinutes"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.EXPIRE_IN_MINUTES.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.EXPIRE_IN_MINUTES.PLACEHOLDER')
                "
                :message="typebotExpireInMinutesError"
                :message-type="typebotExpireInMinutesError ? 'error' : 'info'"
                @blur="v$.typebotExpireInMinutes?.$touch()"
              />
              <Input
                id="typebot-default-delay"
                v-model="formState.typebotDefaultDelayMessage"
                :label="
                  $t('AGENT_BOTS.FORM.TYPEBOT.DEFAULT_DELAY_MESSAGE.LABEL')
                "
                :placeholder="
                  $t(
                    'AGENT_BOTS.FORM.TYPEBOT.DEFAULT_DELAY_MESSAGE.PLACEHOLDER'
                  )
                "
                :message="typebotDefaultDelayMessageError"
                :message-type="
                  typebotDefaultDelayMessageError ? 'error' : 'info'
                "
                @blur="v$.typebotDefaultDelayMessage?.$touch()"
              />
              <Input
                id="typebot-debounce"
                v-model="formState.typebotDebounceTime"
                :label="$t('AGENT_BOTS.FORM.TYPEBOT.DEBOUNCE_TIME.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.TYPEBOT.DEBOUNCE_TIME.PLACEHOLDER')
                "
                :message="typebotDebounceTimeError"
                :message-type="typebotDebounceTimeError ? 'error' : 'info'"
                @blur="v$.typebotDebounceTime?.$touch()"
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.TITLE') }}
              </h3>
              <div
                class="flex items-start justify-between gap-3 rounded-lg border border-n-slate-6 bg-n-slate-1 px-3 py-3"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IGNORE_GROUPS_HELP_TEXT'
                      )
                    }}
                  </span>
                </div>
                <Switch
                  v-model="formState.typebotIgnoreGroups"
                  :aria-label="
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                    )
                  "
                />
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IDENTIFIERS_LABEL'
                    )
                  }}
                </span>
                <TagInput
                  v-model="formState.typebotIgnoredTargets"
                  allow-create
                  :placeholder="
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IDENTIFIERS_PLACEHOLDER'
                    )
                  "
                />
                <p class="text-xs text-n-slate-11">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.TYPEBOT.IGNORED_CONTACTS.IDENTIFIERS_HELP_TEXT'
                    )
                  }}
                </p>
              </div>
            </div>
          </div>

          <!-- Ai Focus Configuration -->
          <div
            v-if="formState.botType === BOT_TYPES.EVO_AI"
            class="flex flex-col gap-6"
          >
            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.EVO_AI.SECTIONS.CONNECTION') }}
              </h3>
              <Input
                id="evo-ai-agent-url"
                v-model="formState.evoAiAgentUrl"
                :label="$t('AGENT_BOTS.FORM.EVO_AI.AGENT_URL.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.EVO_AI.AGENT_URL.PLACEHOLDER')
                "
                :message="
                  v$.evoAiAgentUrl?.$error
                    ? v$.evoAiAgentUrl.$errors[0]?.$message
                    : ''
                "
                :message-type="v$.evoAiAgentUrl?.$error ? 'error' : 'info'"
                @blur="v$.evoAiAgentUrl?.$touch()"
              />
              <Input
                id="evo-ai-api-key"
                v-model="formState.evoAiApiKey"
                :label="$t('AGENT_BOTS.FORM.EVO_AI.API_KEY.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.EVO_AI.API_KEY.PLACEHOLDER')"
                type="password"
                :message="
                  v$.evoAiApiKey?.$error
                    ? v$.evoAiApiKey.$errors[0]?.$message
                    : ''
                "
                :message-type="v$.evoAiApiKey?.$error ? 'error' : 'info'"
                @blur="v$.evoAiApiKey?.$touch()"
              />
              <Input
                id="evo-ai-agent-id"
                v-model="formState.evoAiAgentId"
                :label="$t('AGENT_BOTS.FORM.EVO_AI.AGENT_ID.LABEL')"
                :placeholder="$t('AGENT_BOTS.FORM.EVO_AI.AGENT_ID.PLACEHOLDER')"
                :message="
                  v$.evoAiAgentId?.$error
                    ? v$.evoAiAgentId.$errors[0]?.$message
                    : ''
                "
                :message-type="v$.evoAiAgentId?.$error ? 'error' : 'info'"
                @blur="v$.evoAiAgentId?.$touch()"
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.EVO_AI.TRIGGER_SETTINGS.TITLE') }}
              </h3>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AGENT_BOTS.FORM.EVO_AI.TRIGGER_TYPE.LABEL') }}
                </span>
                <SelectMenu
                  :model-value="formState.evoAiTriggerType"
                  :options="triggerTypeOptions"
                  :label="selectedTriggerTypeLabel"
                  sub-menu-position="bottom"
                  @update:model-value="
                    value => (formState.evoAiTriggerType = value)
                  "
                />
                <p
                  v-if="v$.evoAiTriggerType?.$error"
                  class="text-xs text-n-ruby-9"
                >
                  {{ v$.evoAiTriggerType.$errors[0]?.$message }}
                </p>
              </div>

              <div
                v-if="formState.evoAiTriggerType === 'keyword'"
                class="flex flex-col gap-3"
              >
                <div class="flex flex-col gap-2">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{ $t('AGENT_BOTS.FORM.EVO_AI.TRIGGER_OPERATOR.LABEL') }}
                  </span>
                  <SelectMenu
                    :model-value="formState.evoAiTriggerOperator"
                    :options="triggerOperatorOptions"
                    :label="selectedTriggerOperatorLabel"
                    sub-menu-position="bottom"
                    @update:model-value="
                      value => (formState.evoAiTriggerOperator = value)
                    "
                  />
                  <p
                    v-if="v$.evoAiTriggerOperator?.$error"
                    class="text-xs text-n-ruby-9"
                  >
                    {{ v$.evoAiTriggerOperator.$errors[0]?.$message }}
                  </p>
                </div>
                <Input
                  id="evo-ai-trigger-value"
                  v-model="formState.evoAiTriggerValue"
                  :label="$t('AGENT_BOTS.FORM.EVO_AI.TRIGGER_VALUE.LABEL')"
                  :placeholder="
                    $t('AGENT_BOTS.FORM.EVO_AI.TRIGGER_VALUE.PLACEHOLDER')
                  "
                  :message="
                    v$.evoAiTriggerValue?.$error
                      ? v$.evoAiTriggerValue.$errors[0]?.$message
                      : ''
                  "
                  :message-type="
                    v$.evoAiTriggerValue?.$error ? 'error' : 'info'
                  "
                  @blur="v$.evoAiTriggerValue?.$touch()"
                />
              </div>
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.EVO_AI.GENERAL_SETTINGS.TITLE') }}
              </h3>
              <Input
                id="evo-ai-finish-keyword"
                v-model="formState.evoAiFinishKeyword"
                :label="$t('AGENT_BOTS.FORM.EVO_AI.FINISH_KEYWORD.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.EVO_AI.FINISH_KEYWORD.PLACEHOLDER')
                "
                :message="$t('AGENT_BOTS.FORM.EVO_AI.FINISH_KEYWORD.HELP_TEXT')"
              />
              <Input
                id="evo-ai-expire"
                v-model="formState.evoAiExpireInMinutes"
                :label="$t('AGENT_BOTS.FORM.EVO_AI.EXPIRE_IN_MINUTES.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.EVO_AI.EXPIRE_IN_MINUTES.PLACEHOLDER')
                "
                :message="
                  v$.evoAiExpireInMinutes?.$error
                    ? v$.evoAiExpireInMinutes.$errors[0]?.$message
                    : ''
                "
                :message-type="
                  v$.evoAiExpireInMinutes?.$error ? 'error' : 'info'
                "
                @blur="v$.evoAiExpireInMinutes?.$touch()"
              />
              <Input
                id="evo-ai-default-delay"
                v-model="formState.evoAiDefaultDelayMessage"
                :label="
                  $t('AGENT_BOTS.FORM.EVO_AI.DEFAULT_DELAY_MESSAGE.LABEL')
                "
                :placeholder="
                  $t('AGENT_BOTS.FORM.EVO_AI.DEFAULT_DELAY_MESSAGE.PLACEHOLDER')
                "
                :message="
                  v$.evoAiDefaultDelayMessage?.$error
                    ? v$.evoAiDefaultDelayMessage.$errors[0]?.$message
                    : ''
                "
                :message-type="
                  v$.evoAiDefaultDelayMessage?.$error ? 'error' : 'info'
                "
                @blur="v$.evoAiDefaultDelayMessage?.$touch()"
              />
              <Input
                id="evo-ai-debounce"
                v-model="formState.evoAiDebounceTime"
                :label="$t('AGENT_BOTS.FORM.EVO_AI.DEBOUNCE_TIME.LABEL')"
                :placeholder="
                  $t('AGENT_BOTS.FORM.EVO_AI.DEBOUNCE_TIME.PLACEHOLDER')
                "
                :message="
                  v$.evoAiDebounceTime?.$error
                    ? v$.evoAiDebounceTime.$errors[0]?.$message
                    : ''
                "
                :message-type="v$.evoAiDebounceTime?.$error ? 'error' : 'info'"
                @blur="v$.evoAiDebounceTime?.$touch()"
              />
            </div>

            <div class="flex flex-col gap-3">
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.TITLE') }}
              </h3>
              <div
                class="flex items-start justify-between gap-3 rounded-lg border border-n-slate-6 bg-n-slate-1 px-3 py-3"
              >
                <div class="flex flex-col gap-1">
                  <span class="text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                      )
                    }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{
                      $t(
                        'AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.IGNORE_GROUPS_HELP_TEXT'
                      )
                    }}
                  </span>
                </div>
                <Switch
                  v-model="formState.evoAiIgnoreGroups"
                  :aria-label="
                    $t(
                      'AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.IGNORE_GROUPS_LABEL'
                    )
                  "
                />
              </div>
              <div class="flex flex-col gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.IDENTIFIERS_LABEL'
                    )
                  }}
                </span>
                <TagInput
                  v-model="formState.evoAiIgnoredTargets"
                  allow-create
                  :placeholder="
                    $t(
                      'AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.IDENTIFIERS_PLACEHOLDER'
                    )
                  "
                />
                <p class="text-xs text-n-slate-11">
                  {{
                    $t(
                      'AGENT_BOTS.FORM.EVO_AI.IGNORED_CONTACTS.IDENTIFIERS_HELP_TEXT'
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showAccessTokenInput" class="flex flex-col gap-1">
          <label
            v-if="type === MODAL_TYPES.EDIT"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ $t('AGENT_BOTS.ACCESS_TOKEN.TITLE') }}
          </label>
          <AccessToken
            v-if="type === MODAL_TYPES.EDIT"
            :value="accessToken"
            @on-copy="onCopyToken"
            @on-reset="onResetToken"
          />
          <AccessToken
            v-else
            :value="accessToken"
            :show-reset-button="false"
            @on-copy="onCopyToken"
          />
        </div>
      </div>

      <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('AGENT_BOTS.FORM.CANCEL')"
          @click="onClickClose()"
        />
        <NextButton
          v-if="!showAccessToken"
          type="submit"
          data-testid="label-submit"
          :label="confirmButtonLabel"
          :is-loading="isLoading"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </Dialog>
</template>
